import { Link, useNavigate, useParams } from "react-router-dom";
import UnlimitBreadcrumb from "../../../components/ui-kit/unlimit-breadcrumb";
import httpService from "../../../services/http/sales/sales.service";
import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import UnlimitLoading from "../../../components/ui-kit/unlimit-loading";
import UnlimitFieldDetails from "../../../components/ui-kit/unlimit-field-details";
import UnlimitTable from "../../../components/ui-kit/unlimit-table";
import usePagination from "../../../custom-hooks/usePagination";
import ConfirmationAlert from "../../../components/confirm-alert";
import ImageWithBasePath from "../../../core/img/imagewithbasebath";
import { Upload } from "feather-icons-react/build/IconComponents";
import AddDocuments from "../../../components/add-documents";
import UnlimitHyperlink from "../../../components/ui-kit/unlimit-hyperlink";
import notificationService from "../../../services/toaster.service";
import { useTranslation } from "react-i18next";
import moment from "moment";
import formatNumber from "../../../utils/numberFunction";
import { useSelectedCountry } from "../../../feature-module/dashboard/hooks/useSelectedCountry";
import { all_routes } from "../../../Router/all_routes";

export default function SalesDetails() {
  const [pageLoading, setPageLoading] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { id } = useParams();
  const [sale, setSale] = useState([]);
  const selectedCountry = useSelectedCountry();
  const { t } = useTranslation();
  const { paginationParams, updatePaginationParams } = usePagination({
    pagination: {
      current: 1,
      pageSize: 10,
      showSizeChanger: true,
      showTotal: (total, range) =>
        `${range[0]}-${range[1]} ${t("pagination.of")} ${total} ${t(
          "pagination.items"
        )}`,
    },
  });

  const getSale = async () => {
    setPageLoading(true);
    try {
      const response = await httpService.getSales(id);
      if (response && response.status === 200) {
        setSale(response.data.data);
        updatePaginationParams({
          pagination: {
            ...paginationParams.pagination,
            total: response.data.data.length,
          },
        });
      } else {
        navigate(all_routes.salesList);
      }
    } catch (error) {
      console.log(error);
      navigate(all_routes.salesList);
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      getSale();
    }
  }, [id]);

  const handleTableChange = async (pagination, filters, sorter) => {
    updatePaginationParams({
      pagination,
      filters,
      sorter,
    });
  };

  const columns = [
    {
      title: t("tableColumnName.srNo"),
      dataIndex: "index",
      render: (value, item, index) =>
        (paginationParams?.pagination?.current - 1) *
          paginationParams?.pagination?.pageSize +
        (index + 1),
    },
    {
      title: t("inputField.label.EAN"),
      dataIndex: "ean",
      render: (text, record) => record?.product?.ean,
    },
    {
      title: t("tableColumnName.lmProductNo"),
      dataIndex: "ean",
      render: (text, record) => record?.product?.lm_product_number,
    },
    {
      title: t("tableColumnName.productName"),
      dataIndex: "name",
      render: (text, record) => (
        <div className="d-flex align-items-center">
          <ImageWithBasePath
            type="link"
            src={record?.product?.main_product_image}
            alt="Product"
            style={{ width: 30, height: 30, marginRight: 10 }}
          />
          {record?.product?.name}
        </div>
      ),
    },
    {
      title: t("tableColumnName.uomCode"),
      dataIndex: "ean",
      render: (text, record) =>
        record?.uom_code
          ? `${record?.uom_code} ${
              record?.uom_value ? "(" + record?.uom_value + ")" : ""
            }`
          : "-",
    },
    {
      title: t("tableColumnName.quantity"),
      dataIndex: "quantity",
      align: "center",
    },
  ];

  // Paginate data client-side
  const paginate = (data, current, pageSize) => {
    const startIndex = (current - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return data?.slice(startIndex, endIndex);
  };

  const paginatedData = paginate(
    sale.sale_items,
    paginationParams.pagination.current,
    paginationParams.pagination.pageSize
  );

  const breadcrumbItems = [
    { label: "", link: "/", icon: "feather-home" },
    { label: t("Sales.Sales"), link: "/sales-list" },
    { label: t("Sales.SalesDetails"), active: true },
  ];

  const onHideModal = async () => {
    console.log("hide modal");
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex flex-column">
              <div className="page-title">
                <h3 className="text-black">{t("Sales.SalesDetails")}</h3>
              </div>
              <UnlimitBreadcrumb items={breadcrumbItems} />
            </div>
          </div>
          {pageLoading ? (
            <div className="row d-flex justify-content-center">
              <UnlimitLoading width={50} height={50} />
            </div>
          ) : (
            sale && (
              <>
                <div className="">
                  <div className="p-3 card">
                    <div className="row mb-md-3">
                      <div className="col-sm-3 ">
                        <UnlimitFieldDetails
                          heading={t("Sales.SalesID")}
                          details={sale.order_code}
                        />
                      </div>

                      <div className="col-sm-3 ">
                        <UnlimitFieldDetails
                          heading={"Date"}
                          details={moment(sale.created_at).format(
                            "DD MMM YYYY - HH:mm:ss"
                          )}
                        />
                      </div>
                      <div className="col-sm-3 ">
                        <UnlimitFieldDetails
                          heading={t("shop.shop")}
                          details={
                            <div className="d-flex align-items-center w-100 justify-content-between">
                              <div className="d-flex align-items-center">
                                <ImageWithBasePath
                                  type="link"
                                  src={sale.outlet?.image_url}
                                  alt="Contact"
                                  style={{
                                    width: 30,
                                    height: 30,
                                    marginRight: 10,
                                  }}
                                />
                                {sale?.outlet?.name}
                              </div>
                            </div>
                          }
                        />
                      </div>
                      <div className="col-sm-3">
                        <UnlimitFieldDetails
                          heading={t("Sales.Reference")}
                          details={sale.reference ?? "-"}
                        />
                      </div>

                      <div className="col-sm-3 mt-3">
                        <UnlimitFieldDetails
                          heading={t("tableColumnName.lmAccountNo")}
                          details={sale.company_account?.lm_account_number}
                        />
                      </div>
                      <div className="col-sm-3 mt-3">
                        <UnlimitFieldDetails
                          heading={t("inputField.label.lmUserID")}
                          details={sale.shopper?.lm_shopper_number}
                        />
                      </div>
                      <div className="col-sm-3 mt-3">
                        <UnlimitFieldDetails
                          heading={t("Sales.shopperName")}
                          details={sale?.shopper?.name}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card">
                  <div className="table-responsive">
                    <UnlimitTable
                      pagination={paginationParams.pagination}
                      columns={columns}
                      dataSource={paginatedData}
                      rowKey={(record) => record.id}
                      loading={loading}
                      onChange={handleTableChange}
                    />
                  </div>
                </div>
              </>
            )
          )}
        </div>
        <AddDocuments onHide={onHideModal} />
      </div>
    </>
  );
}
